/* Recipe share dropdown — provides copy-URL + direct social-share intents.
 * Notes:
 *  • Facebook, X (Twitter), WhatsApp, Telegram and Reddit all expose direct
 *    web-share intent URLs we can open in a new tab.
 *  • Instagram and TikTok do NOT support web-share intents — the convention
 *    is to copy the URL to clipboard and prompt the user to paste it inside
 *    those apps' caption/story compose screens.
 *  • The Web Share API path is offered first for mobile users who get the
 *    native OS share sheet (best UX on iOS).
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  ShareNetwork, Copy, FacebookLogo, XLogo, WhatsappLogo, TelegramLogo,
  InstagramLogo, TiktokLogo, Envelope, RedditLogo, LinkSimple,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { EmailShareDialog } from "./EmailShareDialog";

function buildShareIntents({ url, title, text }) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  const tx = encodeURIComponent(text || `Check out this recipe: ${title}`);
  return {
    facebook:  `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    twitter:   `https://twitter.com/intent/tweet?url=${u}&text=${tx}`,
    whatsapp:  `https://wa.me/?text=${tx}%20${u}`,
    telegram:  `https://t.me/share/url?url=${u}&text=${tx}`,
    reddit:    `https://www.reddit.com/submit?url=${u}&title=${t}`,
    email:     `mailto:?subject=${t}&body=${tx}%0A%0A${u}`,
  };
}

export default function ShareRecipeButton({ recipe, url, className = "" }) {
  const [open, setOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const title = recipe?.title || "Recipe";
  const intents = buildShareIntents({ url: shareUrl, title });

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch { toast.error("Couldn't copy — long-press the URL bar to copy."); }
  };

  const nativeShare = async () => {
    if (!navigator.share) { copyUrl(); return; }
    try {
      await navigator.share({ title, text: `Check out this recipe: ${title}`, url: shareUrl });
    } catch { /* user dismissed */ }
  };

  const openIntent = (href) => {
    window.open(href, "_blank", "noopener,noreferrer,width=640,height=580");
  };

  const copyForInstagram = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied — open Instagram and paste it into a Story or caption.", { duration: 5000 });
  };

  const copyForTikTok = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied — open TikTok and paste it into your caption or bio.", { duration: 5000 });
  };

  const supportsNative = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <>
      <button
        data-testid="recipe-share-btn"
        onClick={() => setOpen(true)}
        className={`btn-outline-ink !py-2 !px-3 !text-xs ${className}`}
        aria-label="Share recipe"
      >
        <ShareNetwork size={14} /> Share
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-rf-cream border-rf-ink/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black text-rf-ink">Share recipe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* URL row */}
            <div className="bg-rf-cream-alt rounded-xl px-3 py-3">
              <p className="overline mb-1">Recipe URL</p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  data-testid="recipe-share-url-input"
                  className="input-rf flex-1 !py-2 !text-xs font-mono"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  onClick={copyUrl}
                  data-testid="recipe-share-copy-url"
                  className="btn-amber !py-2 !px-3 !text-xs"
                ><Copy size={14} /> Copy</button>
              </div>
            </div>

            {/* Native share (mobile) */}
            {supportsNative && (
              <button
                onClick={nativeShare}
                data-testid="recipe-share-native"
                className="btn-outline-ink w-full justify-center !py-3"
              >
                <ShareNetwork size={18} weight="bold" /> Open device share sheet
              </button>
            )}

            {/* Social grid */}
            <div>
              <p className="overline mb-2">Post to social</p>
              <div className="grid grid-cols-4 gap-2">
                <SocialTile
                  data-testid="recipe-share-facebook"
                  label="Facebook"
                  Icon={FacebookLogo}
                  onClick={() => openIntent(intents.facebook)}
                />
                <SocialTile
                  data-testid="recipe-share-x"
                  label="X"
                  Icon={XLogo}
                  onClick={() => openIntent(intents.twitter)}
                />
                <SocialTile
                  data-testid="recipe-share-whatsapp"
                  label="WhatsApp"
                  Icon={WhatsappLogo}
                  onClick={() => openIntent(intents.whatsapp)}
                />
                <SocialTile
                  data-testid="recipe-share-telegram"
                  label="Telegram"
                  Icon={TelegramLogo}
                  onClick={() => openIntent(intents.telegram)}
                />
                <SocialTile
                  data-testid="recipe-share-reddit"
                  label="Reddit"
                  Icon={RedditLogo}
                  onClick={() => openIntent(intents.reddit)}
                />
                <SocialTile
                  data-testid="recipe-share-instagram"
                  label="Instagram"
                  Icon={InstagramLogo}
                  onClick={copyForInstagram}
                  hint="copies URL"
                />
                <SocialTile
                  data-testid="recipe-share-tiktok"
                  label="TikTok"
                  Icon={TiktokLogo}
                  onClick={copyForTikTok}
                  hint="copies URL"
                />
                <SocialTile
                  data-testid="recipe-share-email"
                  label="Email"
                  Icon={Envelope}
                  onClick={() => { setOpen(false); setEmailOpen(true); }}
                />
              </div>
              <p className="text-[11px] text-rf-ink-soft mt-2 leading-snug">
                <LinkSimple size={11} className="inline mr-0.5" />
                Instagram &amp; TikTok don&apos;t accept direct web posts — we copy the recipe URL so you can paste it into your caption, Story or bio.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <EmailShareDialog open={emailOpen} onOpenChange={setEmailOpen} title={title} url={shareUrl} summary={`Check out this recipe: ${title}`} kind="recipe" />
    </>
  );
}

function SocialTile({ label, Icon, onClick, hint, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1 p-2 rounded-xl bg-white hover:bg-rf-amber/20 border border-rf-ink/10 transition-all hover:-translate-y-0.5"
      {...rest}
    >
      <Icon size={22} weight="fill" className="text-rf-ink" />
      <span className="text-[10px] font-bold text-rf-ink">{label}</span>
      {hint && <span className="text-[9px] text-rf-ink-soft">{hint}</span>}
    </button>
  );
}
